import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../services/api';

const DEFAULT_COL_WIDTH_PX = 80;

// Excel column widths are stored in "characters" (or occasionally raw pixels).
// This mirrors the standard character-to-pixel approximation for the default font.
const colToPx = (col) => {
  if (!col) return DEFAULT_COL_WIDTH_PX;
  if (col.wpx) return Math.round(col.wpx);
  const wch = col.wch ?? col.width;
  return wch != null ? Math.round(wch * 7 + 5) : DEFAULT_COL_WIDTH_PX;
};

const parseWorkbook = (arrayBuffer) => {
  // cellStyles is required for SheetJS to populate the !rows/!cols hidden & width metadata.
  const workbook = XLSX.read(arrayBuffer, { type: 'array', cellStyles: true });
  const sheets = {};
  workbook.SheetNames.forEach((name) => {
    const worksheet = workbook.Sheets[name];
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    const allRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '', blankrows: true });

    // sheet_to_json returns every row/column regardless of Excel's hidden flag,
    // so filter those out using the worksheet's !rows/!cols metadata.
    const visibleRows = allRows.filter((_, i) => !worksheet['!rows']?.[i + range.s.r]?.hidden);

    const colCount = visibleRows[0]?.length || 0;
    const visibleColIndices = [];
    for (let c = 0; c < colCount; c++) {
      if (!worksheet['!cols']?.[c + range.s.c]?.hidden) visibleColIndices.push(c);
    }

    const allProjectedRows = visibleRows.map((row) => visibleColIndices.map((c) => row[c]));

    // Drop rows where spreadsheet column B has no data (keep the header row regardless).
    const colBRelIndex = XLSX.utils.decode_col('B') - range.s.c;
    const isBlankRow = (row) => {
      const cell = row[colBRelIndex];
      return cell === '' || cell == null;
    };
    const rows = allProjectedRows.length
      ? [
          allProjectedRows[0],
          ...allProjectedRows.slice(1).filter((_, idx) => colBRelIndex < 0 || !isBlankRow(visibleRows[idx + 1])),
        ]
      : [];

    const colWidths = visibleColIndices.map((c) => colToPx(worksheet['!cols']?.[c + range.s.c]));
    sheets[name] = { rows, colWidths };
  });
  return { sheetNames: workbook.SheetNames, sheets };
};

const useExcelPreview = (fetchUrl) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [sheets, setSheets] = useState({});
  const [activeSheet, setActiveSheet] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(fetchUrl, { responseType: 'arraybuffer' });
      const { sheetNames: names, sheets: parsedSheets } = parseWorkbook(res.data);
      setSheetNames(names);
      setSheets(parsedSheets);
      setActiveSheet(names[0] || null);
    } catch (err) {
      setSheetNames([]);
      setSheets({});
      setActiveSheet(null);
      if (err.response?.status === 404) {
        setError('No uploaded data sheet found.');
      } else {
        setError('Failed to load the uploaded data sheet.');
      }
    } finally {
      setLoading(false);
    }
  }, [fetchUrl]);

  const open = useCallback(() => {
    setIsOpen(true);
    load();
  }, [load]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  return {
    isOpen,
    open,
    close,
    loading,
    error,
    sheetNames,
    activeSheet,
    setActiveSheet,
    rows: activeSheet ? sheets[activeSheet]?.rows || [] : [],
    colWidths: activeSheet ? sheets[activeSheet]?.colWidths || [] : [],
  };
};

export default useExcelPreview;
